import React, { useState, useMemo, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
Chart.register(ArcElement, Tooltip, Legend);

export const DEFAULT_BIRTHS = {
  total: 286585,
  liveMale: 149475,
  liveFemale: 137110,
  stillBirth: 1713,
};

// deterministic seeded PRNG (mulberry32)
function mulberry32(a) {
  return function() {
    let t = (a += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// simple hash to number from string (32-bit)
function hashStringToInt(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

// generate deterministic smaller district-level birth data
export function generateDistrictBirthData(districtName, seed = 0) {
  if (!districtName) return DEFAULT_BIRTHS;

  const seedInt = (hashStringToInt(districtName) ^ (seed & 0xffffffff)) >>> 0;
  const rnd = mulberry32(seedInt);

  const total = Math.floor(500 + Math.floor(rnd() * 4500));
  const maleFraction = 0.48 + rnd() * 0.06;
  const liveMale = Math.floor(total * maleFraction);
  const liveFemale = total - liveMale;
  const stillBirth = Math.floor(rnd() * 60);

  return { total, liveMale, liveFemale, stillBirth };
}

// Argon theme colors
const COLORS = ["#5e72e4", "#11cdef", "#f5365c"]; // Male, Female, Still Birth

export default function BirthsCard({ data = DEFAULT_BIRTHS, selectedDistrict = null, seed = 0 }) {
  

  // compute the effectiveData: default (state) OR generated (district)
  const effectiveData = useMemo(() => {
    if (!selectedDistrict) return data;
    return generateDistrictBirthData(selectedDistrict, seed);
  }, [data, selectedDistrict, seed]);
  
  const [open, setOpen] = useState(false);

  const chartData = useMemo(() => ({
    labels: ["Live Male", "Live Female", "Still Birth"],
    datasets: [
      {
        data: [effectiveData.liveMale, effectiveData.liveFemale, effectiveData.stillBirth],
        backgroundColor: COLORS,
        hoverOffset: 8,
      },
    ],
  }), [effectiveData]);
  useEffect(() => {
    console.log("District:", selectedDistrict, "Seed:", seed, "Data:", effectiveData);
  }, [selectedDistrict, seed, effectiveData]);
  const chartOptions = {
    responsive: true,
    plugins: {
      datalabels: false,
      legend: { position: "bottom", labels: { font: { size: 12 } } },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.raw;
            const percentage = ((value / effectiveData.total) * 100).toFixed(2);
            return `${context.label}: ${value.toLocaleString('en-IN')} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-3 px-3 py-2 rounded-lg shadow-sm bg-white border hover:shadow-md w-full"
        aria-expanded={open}
        aria-controls="births-offcanvas"
      >
        <div
          className="w-10 h-10 flex items-center justify-center rounded-md"
          style={{ background: "#f0f4ff" }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2C13.1046 2 14 2.89543 14 4C14 5.10457 13.1046 6 12 6C10.8954 6 10 5.10457 10 4C10 2.89543 10.8954 2 12 2ZM6 22C6 18.6863 8.68629 16 12 16C15.3137 16 18 18.6863 18 22H6Z"
              fill="#5e72e4"
            />
          </svg>
        </div>

        <div className="text-left">
          <div className="text-xs text-slate-500">Total Births</div>
          <div className="text-lg font-semibold">{effectiveData.total.toLocaleString('en-IN')}</div>
        </div>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
            aria-hidden
          />

          <aside
            id="births-offcanvas"
            className="relative ml-auto w-full max-w-2xl h-full bg-white shadow-2xl overflow-auto"
          >
            <div className="p-4 border-b flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold">Births</h3>
                <p className="text-xs text-slate-500">
                  {selectedDistrict ? `District: ${selectedDistrict}` : "State-level summary"}
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="px-3 py-1 rounded bg-slate-100 text-sm"
              >
                Close
              </button>
            </div>

            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-50 p-3 rounded-lg flex items-center justify-center">
                <Pie data={chartData} options={chartOptions} height={300} />
              </div>

              <div className="flex flex-col gap-2">
                {[
                  { label: "Live Male", value: effectiveData.liveMale, color: COLORS[0] },
                  { label: "Live Female", value: effectiveData.liveFemale, color: COLORS[1] },
                  { label: "Still Birth", value: effectiveData.stillBirth, color: COLORS[2] },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="p-3 bg-white rounded border flex justify-between items-center"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ background: item.color }} />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                    <div className="font-semibold">{item.value.toLocaleString('en-IN')}</div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
